import QRCode from "qrcode";
import JSZip from "jszip";
import { jsPDF } from "jspdf";

import { MAU, XANH_LA, SEMANTIC, FONT_CHU, FONT_HIEU } from "assets/theme/avocadoBrand";
import logoThuMuaBo from "assets/images/logo-thu-mua-bo.png";

/** Thẻ name card 90×54 mm @ 300 dpi. */
export const THE_RONG_MM = 90;
export const THE_CAO_MM = 54;
const DPI = 300;
export const THE_RONG_PX = Math.round((THE_RONG_MM / 25.4) * DPI); // ~1063
export const THE_CAO_PX = Math.round((THE_CAO_MM / 25.4) * DPI); // ~638

const A4_RONG_MM = 210;
const A4_CAO_MM = 297;
const SO_COT = 2;
const SO_HANG = 4;
const THE_TREN_TRANG = SO_COT * SO_HANG;

/** Vàng kim logo LV — accent in thẻ (tiết kiệm mực: chỉ nét/chữ nhỏ). */
const VANG_LV = MAU.pit;

let anhLogoCache = null;
let anhLogoTrongSuotCache = null;

function taiAnh(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Không tải được ảnh thẻ khách hàng."));
    img.src = src;
  });
}

async function layAnhLogo() {
  if (anhLogoCache) return anhLogoCache;
  anhLogoCache = await taiAnh(logoThuMuaBo);
  return anhLogoCache;
}

/** Bỏ nền đen của file logo để embleme xanh–vàng nổi trên thẻ trắng. */
async function layAnhLogoTrongSuot() {
  if (anhLogoTrongSuotCache) return anhLogoTrongSuotCache;
  const goc = await layAnhLogo();
  const off = document.createElement("canvas");
  off.width = goc.naturalWidth || goc.width;
  off.height = goc.naturalHeight || goc.height;
  const o = off.getContext("2d");
  o.drawImage(goc, 0, 0);
  const data = o.getImageData(0, 0, off.width, off.height);
  const px = data.data;
  for (let i = 0; i < px.length; i += 4) {
    if (px[i] < 36 && px[i + 1] < 36 && px[i + 2] < 36) px[i + 3] = 0;
  }
  o.putImageData(data, 0, 0);
  anhLogoTrongSuotCache = await taiAnh(off.toDataURL("image/png"));
  return anhLogoTrongSuotCache;
}

async function doiFontSanSang() {
  if (typeof document !== "undefined" && document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      /* bỏ qua — fallback Georgia/Inter hệ thống */
    }
  }
}

/** Địa chỉ in thẻ: "xa, tinh" → diaChi → "—". */
export function formatDiaChiThe(cv) {
  const xa = (cv?.xa || "").trim();
  const tinh = (cv?.tinh || "").trim();
  if (xa && tinh) return `${xa}, ${tinh}`;
  if (xa || tinh) return xa || tinh;
  const diaChi = (cv?.diaChi || "").trim();
  return diaChi || "—";
}

function veBoTron(ctx, x, y, w, h, r) {
  const banKinh = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, w, h, banKinh);
  } else {
    ctx.moveTo(x + banKinh, y);
    ctx.arcTo(x + w, y, x + w, y + h, banKinh);
    ctx.arcTo(x + w, y + h, x, y + h, banKinh);
    ctx.arcTo(x, y + h, x, y, banKinh);
    ctx.arcTo(x, y, x + w, y, banKinh);
    ctx.closePath();
  }
}

/** Vẽ "Nhãn: giá trị" cùng một dòng; thu nhỏ font nếu dài (tránh cắt …). */
function veDongMotHang(ctx, { x, y, rong, nhan, giaTri, fontSize, mauGiaTri, fontFamily }) {
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  const nhanChu = `${nhan}: `;
  let co = fontSize;
  const toiThieu = Math.max(10, Math.round(fontSize * 0.72));
  let chuoi = `${nhanChu}${giaTri}`;

  while (co > toiThieu) {
    ctx.font = `600 ${co}px ${fontFamily}`;
    if (ctx.measureText(chuoi).width <= rong) break;
    co -= 1;
  }

  ctx.font = `600 ${co}px ${fontFamily}`;
  if (ctx.measureText(chuoi).width > rong) {
    // Vẫn dài: giữ nhãn, rút gọn giá trị có kiểm soát
    const rongNhan = ctx.measureText(nhanChu).width;
    const rongCon = Math.max(8, rong - rongNhan);
    let gt = String(giaTri || "");
    while (gt.length > 1 && ctx.measureText(`${gt}…`).width > rongCon) gt = gt.slice(0, -1);
    chuoi = `${nhanChu}${gt}…`;
  }

  const viTriHaiCham = chuoi.indexOf(": ");
  if (viTriHaiCham >= 0) {
    const phanNhan = chuoi.slice(0, viTriHaiCham + 2);
    const phanGiaTri = chuoi.slice(viTriHaiCham + 2);
    ctx.fillStyle = SEMANTIC.textSecondary;
    ctx.fillText(phanNhan, x, y);
    const dx = ctx.measureText(phanNhan).width;
    ctx.fillStyle = mauGiaTri;
    ctx.font = `700 ${co}px ${fontFamily}`;
    ctx.fillText(phanGiaTri, x + dx, y);
  } else {
    ctx.fillStyle = mauGiaTri;
    ctx.fillText(chuoi, x, y);
  }
  return y + co + Math.round(co * 0.45);
}

/**
 * Vẽ 1 thẻ lên canvas — name card nông nghiệp (trắng + avocado + vàng LV).
 * Layout: sọc xanh trái · QR · cột chữ đầy đủ (SĐT/Mã xếp dọc, không cắt ngang).
 * @param {object} cv — { maChuVuon, hoTen, sdt, tinh, xa, diaChi }
 */
export async function veTheKhachHangCanvas(cv) {
  const ma = String(cv?.maChuVuon || "").trim();
  if (!ma) throw new Error("Thiếu mã chủ vườn để tạo thẻ.");

  await doiFontSanSang();

  const [anhQr, anhLogo] = await Promise.all([
    taiAnh(
      await QRCode.toDataURL(ma, {
        width: 640,
        margin: 1,
        errorCorrectionLevel: "M",
        color: { dark: MAU.ink, light: "#FFFFFF" },
      })
    ),
    layAnhLogoTrongSuot(),
  ]);

  const canvas = document.createElement("canvas");
  canvas.width = THE_RONG_PX;
  canvas.height = THE_CAO_PX;
  const ctx = canvas.getContext("2d");

  // Nền trắng — tiết kiệm mực khi in hàng loạt
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, THE_RONG_PX, THE_CAO_PX);

  // Sọc brand trái (chữ ký thẻ) + chỉ vàng kim mỏng
  const rongSoc = Math.round(THE_RONG_PX * 0.028);
  ctx.fillStyle = XANH_LA.main;
  ctx.fillRect(0, 0, rongSoc, THE_CAO_PX);
  ctx.fillStyle = VANG_LV;
  ctx.fillRect(rongSoc, 0, Math.max(2, Math.round(rongSoc * 0.28)), THE_CAO_PX);

  // Viền ngoài rất nhẹ (giữ mép khi cắt)
  ctx.strokeStyle = SEMANTIC.border;
  ctx.lineWidth = Math.max(1, Math.round(THE_RONG_PX * 0.002));
  ctx.strokeRect(
    ctx.lineWidth / 2,
    ctx.lineWidth / 2,
    THE_RONG_PX - ctx.lineWidth,
    THE_CAO_PX - ctx.lineWidth
  );

  const pad = Math.round(THE_RONG_PX * 0.028);
  const leTrai = rongSoc + Math.round(rongSoc * 0.45) + pad;

  // —— Cột QR trái (~1/3 bề ngang thẻ cho cân đối) ——
  const qrKhung = Math.round(THE_RONG_PX / 3);
  const qrPad = Math.round(qrKhung * 0.06);
  const qrSize = qrKhung - qrPad * 2;
  const qrKhungX = leTrai;
  const qrKhungY = Math.round((THE_CAO_PX - qrKhung) / 2);

  ctx.fillStyle = MAU.paper;
  veBoTron(ctx, qrKhungX, qrKhungY, qrKhung, qrKhung, Math.round(qrKhung * 0.08));
  ctx.fill();
  ctx.strokeStyle = SEMANTIC.border;
  ctx.lineWidth = Math.max(1, Math.round(THE_RONG_PX * 0.0025));
  veBoTron(ctx, qrKhungX, qrKhungY, qrKhung, qrKhung, Math.round(qrKhung * 0.08));
  ctx.stroke();

  const qrX = qrKhungX + qrPad;
  const qrY = qrKhungY + qrPad;
  ctx.fillStyle = "#FFFFFF";
  veBoTron(ctx, qrX - 1, qrY - 1, qrSize + 2, qrSize + 2, Math.round(qrSize * 0.04));
  ctx.fill();
  ctx.drawImage(anhQr, qrX, qrY, qrSize, qrSize);

  // —— Cột thông tin phải ——
  const textX = qrKhungX + qrKhung + pad;
  const textRong = THE_RONG_PX - pad - textX;

  const logoCao = Math.round(THE_CAO_PX * 0.18);
  const logoTyLe = anhLogo.width / Math.max(anhLogo.height, 1);
  const logoRong = Math.round(logoCao * logoTyLe);
  const logoX = THE_RONG_PX - pad - logoRong;
  const logoY = Math.round(THE_CAO_PX * 0.07);
  ctx.drawImage(anhLogo, logoX, logoY, logoRong, logoCao);

  const fontEyebrow = Math.round(THE_CAO_PX * 0.04);
  const fontNhan = Math.round(THE_CAO_PX * 0.038);
  const fontTenGoc = Math.round(THE_CAO_PX * 0.09);
  const fontDong = Math.round(THE_CAO_PX * 0.055);

  const hoTen = (cv?.hoTen || "").trim() || "—";
  const sdt = (cv?.sdt || "").trim() || "—";
  const diaChi = formatDiaChiThe(cv);

  // Họ tên luôn 1 hàng: dùng full bề ngang cột chữ, thu font nếu dài
  let fontTen = fontTenGoc;
  const fontTenToiThieu = Math.max(18, Math.round(fontTenGoc * 0.62));
  ctx.font = `700 ${fontTen}px ${FONT_HIEU}`;
  while (fontTen > fontTenToiThieu && ctx.measureText(hoTen).width > textRong) {
    fontTen -= 1;
    ctx.font = `700 ${fontTen}px ${FONT_HIEU}`;
  }

  const caoKhoi =
    fontEyebrow +
    Math.round(THE_CAO_PX * 0.028) +
    fontNhan +
    Math.round(fontNhan * 0.22) +
    fontTen +
    Math.round(THE_CAO_PX * 0.042) +
    3 * (fontDong + Math.round(fontDong * 0.45));
  let y = Math.max(logoY + Math.round(logoCao * 0.08), Math.round((THE_CAO_PX - caoKhoi) / 2));

  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillStyle = VANG_LV;
  ctx.font = `700 ${fontEyebrow}px ${FONT_CHU}`;
  ctx.fillText("CHỦ VƯỜN", textX, y);
  y += fontEyebrow + Math.round(THE_CAO_PX * 0.028);

  ctx.fillStyle = SEMANTIC.textSecondary;
  ctx.font = `600 ${fontNhan}px ${FONT_CHU}`;
  ctx.fillText("Họ tên:", textX, y);
  y += fontNhan + Math.round(fontNhan * 0.22);

  ctx.fillStyle = MAU.ink;
  ctx.font = `700 ${fontTen}px ${FONT_HIEU}`;
  ctx.fillText(hoTen, textX, y);
  y += fontTen + Math.round(THE_CAO_PX * 0.016);
  ctx.strokeStyle = VANG_LV;
  ctx.lineWidth = Math.max(1, Math.round(THE_CAO_PX * 0.005));
  ctx.beginPath();
  ctx.moveTo(textX, y);
  ctx.lineTo(textX + Math.min(textRong, Math.round(THE_RONG_PX * 0.32)), y);
  ctx.stroke();
  y += Math.round(THE_CAO_PX * 0.028);

  y = veDongMotHang(ctx, {
    x: textX,
    y,
    rong: textRong,
    nhan: "Địa chỉ",
    giaTri: diaChi,
    fontSize: fontDong,
    mauGiaTri: MAU.ink,
    fontFamily: FONT_CHU,
  });
  y = veDongMotHang(ctx, {
    x: textX,
    y,
    rong: textRong,
    nhan: "SĐT",
    giaTri: sdt,
    fontSize: fontDong,
    mauGiaTri: MAU.ink,
    fontFamily: FONT_CHU,
  });
  veDongMotHang(ctx, {
    x: textX,
    y,
    rong: textRong,
    nhan: "Mã",
    giaTri: ma,
    fontSize: fontDong,
    mauGiaTri: XANH_LA.main,
    fontFamily: FONT_CHU,
  });

  return canvas;
}

export async function taoThePngDataUrl(cv) {
  const canvas = await veTheKhachHangCanvas(cv);
  return canvas.toDataURL("image/png");
}

export async function taoThePngBlob(cv) {
  const canvas = await veTheKhachHangCanvas(cv);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) reject(new Error("Không tạo được ảnh PNG thẻ."));
      else resolve(blob);
    }, "image/png");
  });
}

function taiBlob(blob, tenFile) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = tenFile;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

function taiDataUrl(dataUrl, tenFile) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = tenFile;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function tenFileAnToan(ma) {
  return String(ma || "the")
    .replace(/[^\w.-]+/g, "_")
    .slice(0, 80);
}

export async function taiThePng(cv) {
  const dataUrl = await taoThePngDataUrl(cv);
  taiDataUrl(dataUrl, `the-khach-hang-${tenFileAnToan(cv.maChuVuon)}.png`);
}

function mmLeTraiPhai() {
  return (A4_RONG_MM - SO_COT * THE_RONG_MM) / 2;
}

function mmLeTrenDuoi() {
  return (A4_CAO_MM - SO_HANG * THE_CAO_MM) / 2;
}

/**
 * PDF 1 thẻ (trang A4 chứa đúng 1 thẻ góc trên trái trong lưới 2×4) hoặc nhiều thẻ 8/trang.
 */
export async function taoPdfThe(danhSachCv) {
  const list = Array.isArray(danhSachCv) ? danhSachCv : [danhSachCv];
  if (list.length === 0) throw new Error("Chưa chọn chủ vườn nào.");

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const leX = mmLeTraiPhai();
  const leY = mmLeTrenDuoi();

  for (let i = 0; i < list.length; i += 1) {
    const viTriTrongTrang = i % THE_TREN_TRANG;
    if (i > 0 && viTriTrongTrang === 0) doc.addPage();

    const cot = viTriTrongTrang % SO_COT;
    const hang = Math.floor(viTriTrongTrang / SO_COT);
    const x = leX + cot * THE_RONG_MM;
    const y = leY + hang * THE_CAO_MM;

    const dataUrl = await taoThePngDataUrl(list[i]);
    doc.addImage(dataUrl, "PNG", x, y, THE_RONG_MM, THE_CAO_MM);

    // Đường cắt đứt nhẹ quanh ô thẻ
    doc.setDrawColor(180, 180, 180);
    doc.setLineDashPattern([1.2, 1.2], 0);
    doc.setLineWidth(0.15);
    doc.rect(x, y, THE_RONG_MM, THE_CAO_MM);
    doc.setLineDashPattern([], 0);
  }

  return doc;
}

export async function taiThePdf(cv) {
  const doc = await taoPdfThe([cv]);
  doc.save(`the-khach-hang-${tenFileAnToan(cv.maChuVuon)}.pdf`);
}

export async function taiPdfNhieuThe(danhSachCv) {
  const doc = await taoPdfThe(danhSachCv);
  const ngay = new Date().toISOString().slice(0, 10);
  doc.save(`the-khach-hang-${ngay}.pdf`);
}

export async function taiZipThePng(danhSachCv) {
  if (!danhSachCv?.length) throw new Error("Chưa chọn chủ vườn nào.");
  const zip = new JSZip();
  for (const cv of danhSachCv) {
    const dataUrl = await taoThePngDataUrl(cv);
    zip.file(`the-${tenFileAnToan(cv.maChuVuon)}.png`, dataUrl.split(",")[1], { base64: true });
  }
  const blob = await zip.generateAsync({ type: "blob" });
  const ngay = new Date().toISOString().slice(0, 10);
  taiBlob(blob, `the-khach-hang-${ngay}.zip`);
}

/** Cửa sổ in HTML: A4, 2 cột × 4 hàng, đường cắt đứt. */
export async function inHangLoatThe(danhSachCv) {
  if (!danhSachCv?.length) throw new Error("Chưa chọn chủ vườn nào.");

  const cuaSo = window.open("", "_blank");
  if (!cuaSo) {
    throw new Error("Trình duyệt đã chặn cửa sổ in — cho phép popup cho trang này rồi thử lại.");
  }

  const items = [];
  for (const cv of danhSachCv) {
    items.push({
      ma: cv.maChuVuon,
      src: await taoThePngDataUrl(cv),
    });
  }

  const leX = mmLeTraiPhai();
  const leY = mmLeTrenDuoi();
  const trangHtml = [];
  for (let i = 0; i < items.length; i += THE_TREN_TRANG) {
    const chunk = items.slice(i, i + THE_TREN_TRANG);
    const oThe = chunk
      .map((it, idx) => {
        const cot = idx % SO_COT;
        const hang = Math.floor(idx / SO_COT);
        const left = leX + cot * THE_RONG_MM;
        const top = leY + hang * THE_CAO_MM;
        return `<div class="the" style="left:${left}mm;top:${top}mm"><img src="${it.src}" alt="${it.ma}" /></div>`;
      })
      .join("");
    trangHtml.push(`<section class="trang">${oThe}</section>`);
  }

  const html = `<!DOCTYPE html><html lang="vi"><head><meta charset="utf-8" />
<title>In thẻ khách hàng chủ vườn</title>
<style>
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #fff; }
  .trang {
    position: relative;
    width: ${A4_RONG_MM}mm;
    height: ${A4_CAO_MM}mm;
    page-break-after: always;
    overflow: hidden;
  }
  .trang:last-child { page-break-after: auto; }
  .the {
    position: absolute;
    width: ${THE_RONG_MM}mm;
    height: ${THE_CAO_MM}mm;
    border: 0.2mm dashed #b0b0b0;
  }
  .the img {
    width: 100%;
    height: 100%;
    display: block;
  }
</style>
</head><body>
${trangHtml.join("")}
<script>window.onload = () => { window.focus(); window.print(); };</script>
</body></html>`;

  cuaSo.document.write(html);
  cuaSo.document.close();
}
